import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import SectionHeader from "@/components/common/SectionHeader";
import { api } from "@/lib/api";

export default function LatestNews() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/news", { params: { limit: 3 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Fallback placeholder if no news yet
  const displayItems = items.length > 0 ? items : PLACEHOLDER_NEWS;

  return (
    <section className="section-y bg-white" data-testid="home-latest-news">
      <div className="container-sf">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <SectionHeader
            eyebrow="Aktivitas Terbaru"
            title="Cerita dari"
            accentTitle="lapangan"
            description="Kabar terbaru dari program, kegiatan, dan inisiatif Sauyunan Foundation di seluruh Nusantara."
          />
          <Link to="/berita" className="btn-outline shrink-0" data-testid="news-see-all">
            Semua Berita <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {displayItems.map((n, i) => (
            <Link
              key={n.slug || i}
              to={items.length ? `/berita/${n.slug}` : "/berita"}
              className="card-sf overflow-hidden group flex flex-col"
              data-testid={`news-card-${i}`}
            >
              <div className="aspect-[4/2.5] overflow-hidden bg-slate-100">
                <img
                  src={n.cover_image || `https://images.unsplash.com/photo-1747778148316-4a8eeb132d48?w=900&q=80&sig=${i}`}
                  alt={n.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                  <span className="inline-flex items-center rounded-full bg-[var(--brand-soft-blue)] text-[var(--brand-blue)] px-3 py-1 font-semibold uppercase tracking-wider text-[10px]">
                    {n.category || "Kegiatan"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(n.published_at)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-[var(--brand-blue)] transition line-clamp-2">
                  {n.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                  {n.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-[var(--brand-orange)] group-hover:gap-3 transition-all">
                  Baca selengkapnya <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const PLACEHOLDER_NEWS = [
  {
    title: "Sauyunan Bioeconomy Initiative Dimulai di Bandung Barat",
    excerpt: "Program terintegrasi berbasis ketahanan pangan dan bioenergi telah resmi diluncurkan bersama komunitas lokal.",
    category: "Program",
    published_at: new Date().toISOString(),
    cover_image: "https://images.pexels.com/photos/15773044/pexels-photo-15773044.jpeg",
  },
  {
    title: "Peluncuran Future Leaders Academy Angkatan Pertama",
    excerpt: "50 pemuda dari 12 provinsi bergabung dalam program pengembangan kepemimpinan berkelanjutan.",
    category: "Pendidikan",
    published_at: new Date().toISOString(),
    cover_image: "https://images.unsplash.com/photo-1549431971-f54b3f5c82d8?w=900&q=80",
  },
  {
    title: "Green Indonesia Initiative Tanam 10.000 Pohon di DAS Citarum",
    excerpt: "Aksi kolaboratif rehabilitasi daerah aliran sungai melibatkan 300+ relawan dan komunitas.",
    category: "Lingkungan",
    published_at: new Date().toISOString(),
    cover_image: "https://images.pexels.com/photos/17565235/pexels-photo-17565235.png",
  },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}
